import React from 'react';
import PropTypes from 'prop-types';
import pick from 'lodash/pick';
import invert from 'lodash/invert';
import { QueryClientProvider, QueryClient } from 'react-query';
import Logger from 'observability/Logger';

import BaseComponent from 'common/components/BaseComponent';
import {
    SuiContainer,
    SuiModalHeader,
    SuiModalContent,
    SuiModalFooter,
    SuiTabs,
    SuiTab,
    SuiSlides,
} from '@splashthat/sui';

import Upload from 'common/icons/Upload';
import Camera from 'common/icons/Camera';
import Giphy from 'common/icons/Giphy';

import SplashCakeClient from 'lib/SplashCakeClient';

import UploadSlide from '../UploadSlide';
import StockSlide from '../StockSlide';
import GifSlide from '../GifSlide';

import { ZERO_WIDTH_SPACE } from '../../utils';
import ALL_TABS, { UPLOAD, STOCK, GIF } from '../../constants/tabs';

import './styles.styl';

const queryClient = new QueryClient();

const GIPHY_LOGO_URL = 'https://d24wuq6o951i2g.cloudfront.net/img/events/splash/powered-by-giphy.png';

const LOG = new Logger('ImageUploader');

export default class ImageUploader extends BaseComponent {
    static propTypes = {
        /* reducers */
        imageUploaderTab: PropTypes.string.isRequired,
        gifSearchValue: PropTypes.string.isRequired,
        gifSearchError: PropTypes.string.isRequired,
        gifs: PropTypes.array.isRequired,
        stockImages: PropTypes.array.isRequired,
        stockImageSearchValue: PropTypes.string,
        stockImageSearchError: PropTypes.string.isRequired,
        stockImageNavValue: PropTypes.string,
        isUploadingImage: PropTypes.bool.isRequired,
        uploadImageError: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        giphyAssetsToCdn: PropTypes.bool.isRequired,
        unsplashAssetsToCdn: PropTypes.bool.isRequired,
        /* actions */
        setImageUploaderTab: PropTypes.func.isRequired,
        setGifSearchValue: PropTypes.func.isRequired,
        setGifs: PropTypes.func.isRequired,
        setGifSearchError: PropTypes.func.isRequired,
        setStockImageSearchValue: PropTypes.func.isRequired,
        setStockImages: PropTypes.func.isRequired,
        setStockImageSearchError: PropTypes.func.isRequired,
        setStockImageNavValue: PropTypes.func.isRequired,
        startImageUpload: PropTypes.func.isRequired,
        finishImageUpload: PropTypes.func.isRequired,
        throwImageUploadError: PropTypes.func.isRequired,
        /* interface */
        onImageSelected: PropTypes.func.isRequired,
        onAsyncUploadToCdnStart: PropTypes.func.isRequired,
        onAsyncUploadToCdnFail: PropTypes.func.isRequired,
        availableTabs: PropTypes.arrayOf(PropTypes.string),
        acceptedMimeTypes: PropTypes.arrayOf(PropTypes.string),
        canUserAccessGiphyAndUnsplash: PropTypes.bool.isRequired,
        uploadSlideCopy: PropTypes.string,
    };
    static defaultProps = {
        availableTabs: [],
        uploadSlideCopy: null,
    };
    static tagName = SuiContainer;

    get baseClassName() {
        return 'ImageUploader';
    }

    constructor(...args) {
        super(...args);

        this.cake = new SplashCakeClient();
    }

    onImageUploaded = (imageUrl, fileExtension) => {
        this.props.finishImageUpload();
        this.selectImage(imageUrl, fileExtension);
    };

    onStockImageSelected = (imageUrl) => {
        this.selectImage(imageUrl);
    };

    onGifImageSelected = (imageUrl) => {
        this.selectImage(imageUrl);
    };

    selectImage = (imageUrl, fileExtension = null) => {
        const { onImageSelected, onAsyncUploadToCdnStart, onAsyncUploadToCdnFail } = this.props;

        const shouldCloseAfterUpload = true;

        if (this.shouldAssetBeUploadedToCdn(imageUrl)) {
            // We might need to upload the Gif to our servers, so we're not done. However,
            // for the sake of perceived speed in the UX, we can show the user that it's done.
            onAsyncUploadToCdnStart(imageUrl);

            // With Giphy, we want to download the image to our S3 buckets because they don't want
            // to have to serve all the images used in our pages. So we push it up.
            this.cake
                .saveImageFromUrl(imageUrl)
                .then((newImageUrl) => {
                    onImageSelected(newImageUrl, !shouldCloseAfterUpload);
                })
                .catch((e) => {
                    onAsyncUploadToCdnFail(e);
                    LOG.error('Error on selectImage', e);
                });
        } else {
            onImageSelected(imageUrl, shouldCloseAfterUpload, fileExtension);
        }
    };

    shouldAssetBeUploadedToCdn = (imageUrl) =>
        (imageUrl.indexOf('unsplash.com/') !== -1 && this.props.unsplashAssetsToCdn) ||
        (imageUrl.indexOf('giphy.com/') !== -1 && this.props.giphyAssetsToCdn);

    propChildren(props, baseClassName) {
        const { imageUploaderTab, setImageUploaderTab, availableTabs, startImageUpload, throwImageUploadError } = props;

        const tabs = invert(availableTabs.length ? availableTabs : ALL_TABS);
        if (!this.props.canUserAccessGiphyAndUnsplash) {
            // remove Giphy and unsplash from object if hide org property set
            delete tabs[GIF];
            delete tabs[STOCK];
        }

        const children = [];

        children.push(
            <SuiModalHeader key="SuiModalHeader">
                <SuiTabs
                    className={`${baseClassName}__navigation`}
                    value={imageUploaderTab}
                    onChange={(event, value) => setImageUploaderTab(value)}
                >
                    {[
                        tabs[UPLOAD] && (
                            <SuiTab
                                label="Upload"
                                value={UPLOAD}
                                iconBefore={Upload}
                                iconDirection="column"
                                key={UPLOAD}
                            />
                        ),
                        tabs[STOCK] && (
                            <SuiTab
                                label="Stock"
                                value={STOCK}
                                iconBefore={Camera}
                                iconDirection="column"
                                key={STOCK}
                            />
                        ),
                        tabs[GIF] && (
                            <SuiTab label="Giphy" value={GIF} iconBefore={Giphy} iconDirection="column" key={GIF} />
                        ),
                    ].filter((i) => i)}
                </SuiTabs>
            </SuiModalHeader>,
        );

        children.push(
            <SuiModalContent className={`${baseClassName}-content`} variation="shaded" key="SuiModalContent">
                <QueryClientProvider client={queryClient}>
                    <SuiSlides slide={imageUploaderTab} variation="full-height">
                        {[
                            tabs[UPLOAD] && (
                                <UploadSlide
                                    slide={UPLOAD}
                                    onImageUploaded={this.onImageUploaded}
                                    onImageUploadStarted={startImageUpload}
                                    onImageUploadError={throwImageUploadError}
                                    {...pick(props, [
                                        'uploadSlideCopy',
                                        'isUploadingImage',
                                        'uploadImageError',
                                        'acceptedMimeTypes',
                                    ])}
                                    key={0}
                                />
                            ),
                            tabs[STOCK] && (
                                <StockSlide
                                    slide={STOCK}
                                    onImageSelected={this.onStockImageSelected}
                                    {...pick(props, [
                                        'stockImages',
                                        'stockImageSearchValue',
                                        'stockImageSearchError',
                                        'setStockImageSearchValue',
                                        'setStockImages',
                                        'setStockImageSearchError',
                                        'setStockImageNavValue',
                                        'stockImageNavValue',
                                    ])}
                                    key={1}
                                />
                            ),
                            tabs[GIF] && (
                                <GifSlide
                                    slide={GIF}
                                    onImageSelected={this.onGifImageSelected}
                                    {...pick(props, [
                                        'gifSearchValue',
                                        'gifs',
                                        'gifSearchError',
                                        'setGifSearchValue',
                                        'setGifs',
                                        'setGifSearchError',
                                    ])}
                                    key={2}
                                />
                            ),
                        ].filter((i) => i)}
                    </SuiSlides>
                </QueryClientProvider>
            </SuiModalContent>,
        );

        let footerContent;
        switch (imageUploaderTab) {
            case GIF:
                footerContent = (
                    <a href="https://giphy.com/" target="_blank">
                        <img src={GIPHY_LOGO_URL} className={`${baseClassName}_footer-image`} alt="Powered by Giphy" />
                    </a>
                );
                break;
            case STOCK:
                footerContent = (
                    <p>
                        Powered by{' '}
                        <a href="https://unsplash.com/" target="_blank">
                            Unsplash
                        </a>
                    </p>
                );
                break;
            default:
                footerContent = (
                    // zero width space so that it takes default line height.
                    <p>{ZERO_WIDTH_SPACE}</p>
                );
        }

        children.push(
            <SuiModalFooter key="SuiModalFooter">
                <SuiContainer variation="flex-group" grow={1} className={`${baseClassName}_footer`}>
                    {footerContent}
                </SuiContainer>
            </SuiModalFooter>,
        );

        return children;
    }
}
